import { useCallback, useEffect, useState } from "react";
import WishlistDropdown from "../../../Wishlist/components/WishlistDropdown";
import { useDispatch, useSelector } from "react-redux";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { redirectToUrl } from "../../../utils/url/urlUtils";
import { sendWishlist } from "../../api/sendWishlist";
import { sendLinearConfigurations } from "../../api/sendLinearConfigurations";
import { Trans, useTranslation } from "react-i18next";
import { Link, LinkButton } from "../../../common/components/Link";

import { type GoogleMarker, type SalesPartnerAddress } from "../../salesPartnerTypes";
import { useIsMaxTabletSize, useIsMinDesktopSize } from "../../../common/hooks/useMediaQuery";
import { CART_PAGE, PRIVACY_PAGE } from "../../../utils/url/staticUrls";
import { CheckboxWithLabel } from "../../../common/components/CheckboxWithLabel";
import { Button } from "~/common/components/Button/Button";
import { trackShareWishlistThunk } from "~/SalesPartner/redux/actions";
import { selectShareableWishlists } from "~/SalesPartner/redux/selectors";
import { Permission, userHasPermission } from "~/utils/permissions";
import { IMG_SALES_PARTNER_FINDER_CART } from "~/utils/url/staticResources";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";

import classes from "./SPCompanyDetailsCardRightRegistered.module.less";
import classNames from "classnames";
import { TextField } from "@mui/material";
import { Alert } from "~/common/m3/Alert/Alert";
import { trackPartnerFinderSearch } from "~/feature/Analytics/ga/salesPartner";
import { transformSelectedMarkerData } from "~/Cart/pages/CartPage/B2CCart/components/B2CRequestPage/utils";
import type { SASearchFormFields } from "../SearchCard/SalesPartnerSearchBox";
import { useFormContext } from "react-hook-form";

interface Props {
  shareLinearConfigurationData?: string[];
  companyDetails: SalesPartnerAddress;
  company: GoogleMarker;
  close?: () => void;
}

const SPCompanyDetailsCardRightRegistered = (props: Props) => {
  const { t } = useTranslation();
  const { companyDetails } = props;

  const wishlists = useSelector(selectShareableWishlists);
  const dispatch = useDispatch();
  const trackShareWishlist = useCallback((salesPartnerName: string, wishlistId: string) => {
    dispatch(trackShareWishlistThunk(salesPartnerName, wishlistId));
  }, []);

  const [wishlistId, setWishlistId] = useState<string | null | undefined>(() =>
    new URLSearchParams(window.location.search).get("wl-id"),
  );
  const [wishlistEntryIds] = useState(
    () => new URLSearchParams(window.location.search).get("wl-entry-ids")?.split(",") ?? [],
  );

  const { setFalse: hideError, setTrue: showError, val: hasError } = useBooleanState(false);
  const [wlValid, setWlValid] = useState(false);

  const { val: checkboxState, toggle: toggleCheckbox } = useBooleanState(false);
  const [comment, setComment] = useState("");

  const { getValues } = useFormContext<SASearchFormFields>();

  const spHasEmail = !!companyDetails.email;

  useEffect(() => {
    setWlValid(!!wishlists.find((wishlist) => wishlist.id === wishlistId));
  }, [wishlistId]);

  let sharedData = null;
  let submitHandler = null;
  let title = null;

  const hasLinearData = props.shareLinearConfigurationData && props.shareLinearConfigurationData.length > 0;

  if (props.shareLinearConfigurationData && props.shareLinearConfigurationData.length) {
    title = t("salesPartner.gmap.card.rightRegistered.title");
    sharedData = (
      <div className={"shared-linear-configs"}>
        {t("page.store.finder.label.shared.configurations", {
          configurationsCount: props.shareLinearConfigurationData.length,
        })}
      </div>
    );
    submitHandler = async () => {
      if (!checkboxState || !props.shareLinearConfigurationData) {
        showError();
        return;
      }
      await sendLinearConfigurations({
        sharedConfigurations: props.shareLinearConfigurationData,
        salesPartnerCode: companyDetails.salesPartnerId,
        salesPartnerMessage: comment,
      });
      redirectToUrl("/store-finder/confirmation");
    };
  } else {
    const isFromWishlistPage = !!wishlistEntryIds.length;
    let adjustedWishlists;
    if (isFromWishlistPage) {
      title = t("page.salesPartnerFinder.share.requestOfferSelected.title");
      adjustedWishlists = wishlists.map((wl) => ({
        ...wl,
        entryIds: wl.id === wishlistId ? wishlistEntryIds : wl.entryIds,
      }));
    } else {
      title = t("salesPartner.gmap.card.rightRegistered.title");
      adjustedWishlists = wishlists;
    }

    sharedData = (
      <WishlistDropdown
        className={"scha__sales-partner__gmaps-sp-card__right__send-wl__wishlist-input"}
        label={t("page.salesPartnerFinder.share.requestOfferSelected.dropdown")}
        wishlists={adjustedWishlists}
        selectedWishlist={wishlistId}
        onWishlistSelect={setWishlistId}
        disabled={!!wishlistEntryIds.length}
      />
    );
    submitHandler = async () => {
      if (!checkboxState || !wishlistId || !wlValid) {
        showError();
        return;
      }
      hideError();

      trackShareWishlist(companyDetails.companyNameDisplay, wishlistId!);
      const selectedWishlist = wishlists.find((wl) => wl.id === wishlistId)!;
      const entryIds = wishlistEntryIds.length > 0 ? wishlistEntryIds : selectedWishlist.entryIds;
      await sendWishlist({
        wishlistId: wishlistId!,
        entryIds: entryIds,
        salesPartnerCode: companyDetails.salesPartnerId,
        salesPartnerMessage: comment,
      });
      redirectToUrl("/store-finder/confirmation");
    };
  }

  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMaxTabletSize();

  const normal = (
    <div className="scha__sales-partner__gmaps-sp-card__right__send-wl">
      <h3 className="scha__sales-partner__gmaps-sp-card__right__send-wl__title">{title}</h3>

      {sharedData}

      <div className="scha__sales-partner__gmaps-sp-card__right__send-wl__comment">
        <TextField
          variant={"outlined"}
          multiline
          fullWidth
          minRows={3}
          maxRows={10}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          label={t("salesPartner.gmap.card.rightRegistered.message-label")}
          data-test-id={"sales-partner_wl-comment"}
        />
      </div>
      <CheckboxWithLabel
        data-test-id={"checkbox-accept-privacy"}
        checked={checkboxState}
        onCheck={() => {
          toggleCheckbox();
          hideError();
        }}
        error={hasError}
        className={"scha__sales-partner__gmaps-sp-card__right__send-wl__checkbox"}
      >
        <Trans i18nKey="salesPartner.message.checkbox">
          I hereby agree to forwarding contact information from my user profile and my comments to the selected sales
          partner for the purpose of contacting and requesting an offer.
          <Link href={PRIVACY_PAGE} target="_blank">
            Privacy Policy
          </Link>
        </Trans>
      </CheckboxWithLabel>
      {hasError && (
        <div className="mb-3">
          <Alert severity="error">{t("salesPartner.gmap.card.rightRegistered.errorMessage")}</Alert>
        </div>
      )}

      <div className="scha__sales-partner__gmaps-sp-card__right__send-wl__actions">
        <div> {t("salesPartner.mandatory")}</div>

        <Button
          fullWidth={false}
          data-test-id={"sales-pratner-register-for-free"}
          disabled={!spHasEmail}
          onClick={submitHandler}
        >
          {t("salesPartner.gmap.card.btn.send")}
        </Button>

        {!isDesktop && (
          <LinkButton className={"scha__sales-partner__gmaps-sp-card__right__send-wl__cancel"} onClick={props.close}>
            {t("cancel")}
          </LinkButton>
        )}
      </div>
    </div>
  );

  const upgraded = (
    <div className="scha__sales-partner__gmaps-sp-card__right__send-wl">
      <h3 className={classes.borderTop}>{t("page.salesPartnerFinder.share.requestOfferSelected.title")}</h3>
      <div className="scha__sales-partner__gmaps-sp-card__right__register">
        {!isTablet && (
          <div className="scha__sales-partner__gmaps-sp-card__right__register__img-container">
            <img className="ml-3" src={IMG_SALES_PARTNER_FINDER_CART} alt="cart" />
          </div>
        )}
        <div className={classes.rightSide}>
          <ol className={classNames(classes.listInside)}>
            <li>
              <Trans i18nKey={"salesPartner.gmap.card.rightRegistered.new.2step"} />
            </li>
            <li>
              <Trans i18nKey={"salesPartner.gmap.card.rightRegistered.new.3step"} />
            </li>
          </ol>
          {isDesktop && (
            <Button
              href={CART_PAGE}
              startIcon={<Icon type={IconType.Cart} />}
              fullWidth
              onClick={() => {
                trackPartnerFinderSearch(
                  getValues(),
                  "Go To Cart Intended",
                  "Click",
                  transformSelectedMarkerData(props.company),
                );
              }}
            >
              {t("addToCartPopup.checkoutBtn")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  if (hasLinearData) {
    return normal;
  }

  return userHasPermission(Permission.ADD_TO_CART) ? upgraded : normal;
};

export default SPCompanyDetailsCardRightRegistered;
